import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { KpiCard } from './mockData';

// הכנסות + הוצאות + רווח in ONE frame (Ohad 2.8): plotting only the net operating
// lines made target and actual look parallel — the gap IS the story, so show the
// two lines whose distance is the profit, and make that distance explicit as ± bars.
// Revenue = estimated actual (current month: EoM forecast); expenses = plan target
// (no live expense actual); profit = the difference.

const GREEN = '#10B981';
const GRAY = '#94A3B8';
const RED = '#EF4444';

export interface RevExpMonth {
  label: string;
  revenue: number;      // estimated actual; current month = EoM forecast
  expenses: number;     // plan target
  profit: number;       // revenue − expenses
  isCurrent?: boolean;  // current month (revenue is a forecast, not an actual)
}

interface Props {
  kpi: KpiCard;
  data: RevExpMonth[];
  onDrill: (to?: string) => void;
  fmt: (n: number) => string;
}

export default function RevExpProfitTile({ kpi, data, onDrill, fmt }: Props) {
  const clickable = !!kpi.drillTo;
  const handleClick = () => { if (clickable) onDrill(kpi.drillTo); };
  const cur = data[data.length - 1];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const isCur = data.find((d) => d.label === label)?.isCurrent;
    return (
      <div style={{ background: '#101D35', border: '1px solid #243457', borderRadius: 6, fontSize: 12, color: '#F1F5F9', direction: 'rtl', padding: '8px 10px', lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}{isCur ? ' · תחזית סוף חודש' : ''}</div>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {payload.map((p: any) => (
          <div key={p.dataKey} style={{ color: p.color === GRAY ? '#E2E8F0' : p.color }}>{p.name}: {fmt(p.value)}</div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`exec-kpi exec-kpi-chart topic-${kpi.topic}${clickable ? ' clickable' : ''}`}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
    >
      <div className="exec-kpi-chart-body">
        <div className="exec-kpi-chart-head">
          <span className="exec-kpi-label">{kpi.label}</span>
          {cur && (
            <span className="exec-kpi-chart-headline">
              <span className="exec-kpi-chart-value" style={{ color: cur.profit >= 0 ? GREEN : RED }}>{fmt(cur.profit)}</span>
              <span className="exec-kpi-chart-meta">{cur.label} · תחזית רווח</span>
            </span>
          )}
        </div>
        <div className="exec-kpi-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 14, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} interval={0} height={20} />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} content={tip} />
              <ReferenceLine y={0} stroke="rgba(148,163,184,0.35)" />
              <Bar dataKey="profit" name="רווח" barSize={14} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {data.map((m) => (
                  <Cell key={m.label} fill={m.profit >= 0 ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.5)'} />
                ))}
              </Bar>
              <Line type="monotone" dataKey="revenue" name="הכנסות" stroke={GREEN} strokeWidth={2.5} dot={{ r: 3, fill: GREEN }} activeDot={{ r: 5 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="expenses" name="הוצאות" stroke={GRAY} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} activeDot={{ r: 4 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="exec-kpi-chart-legend">
          <span className="exec-kpi-chart-legend-item"><span className="exec-kpi-chart-legend-dot" style={{ background: GREEN }} />הכנסות (אומדן)</span>
          <span className="exec-kpi-chart-legend-item"><span className="exec-kpi-chart-legend-dot" style={{ background: GRAY }} />הוצאות (יעד)</span>
          <span className="exec-kpi-chart-legend-item"><span className="exec-kpi-chart-legend-dot" style={{ background: 'rgba(34,197,94,0.45)' }} />רווח</span>
        </div>
      </div>
    </div>
  );
}
